import React, { Suspense, lazy } from 'react';
import { useRoutes, Navigate, Outlet } from 'react-router-dom';
import activePages from './activePage';
import PageTransition, { KeepAliveTransition } from '@/components/PageTransition';
import RouteGuard from '@/components/RouteGuard';
import AwakenApp from '@/pages/awaken-app';

const HomeLayout = lazy(() => import('@/pages/home/Layout'));
const Study = lazy(() => import('@/pages/home/Study'));
const Profile = lazy(() => import('@/pages/home/Profile'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PersonalInfo = lazy(() => import('@/pages/personal-info'));
const Login = lazy(() => import('@/pages/login'));
const SmsVerify = lazy(() => import('@/pages/sms-verify'));
const ModifyPwd = lazy(() => import('@/pages/modify-pwd'));
const ChangeNickname = lazy(() => import('@/pages/change-nickname'));
const CallCenter = lazy(() => import('@/pages/call-center'));
const Suggestion = lazy(() => import('@/pages/suggestion'));
const AddressList = lazy(() => import('@/pages/address-list'));
const AddressEdit = lazy(() => import('@/pages/address-edit'));
const Setting = lazy(() => import('@/pages/setting'));
const Protocol = lazy(() => import('@/pages/protocol'));
const CancelAccount = lazy(() => import('@/pages/cancel-account'));
const CancelAccountConfirm = lazy(() => import('@/pages/cancel-account-confirm'));
const CurrentPhone = lazy(() => import('@/pages/current-phone'));
const VerifyCurrentPhone = lazy(() => import('@/pages/verify-current-phone'));
const ModifyPhone = lazy(() => import('@/pages/modify-phone'));
const ForgetPwd = lazy(() => import('@/pages/forget-pwd'));
const RenewClassCoupon = lazy(() => import('@/pages/renew-class-coupon'));
const StudyCalendar = lazy(() => import('@/pages/study-calendar'));
const MyOrder = lazy(() => import('@/pages/my-order'));
const TrialCourse = lazy(() => import('@/pages/trial-course'));
const DownloadApp = lazy(() => import('@/pages/download-app'));
const CourseDetail = lazy(() => import('@/pages/course-detail'));
const PayConfirm = lazy(() => import('@/pages/pay-confirm'));
const LivePlay = lazy(() => import('@/pages/live-play'));
const OrderDetail = lazy(() => import('@/pages/order-detail'));
const Pay = lazy(() => import('@/pages/pay'));
const AliPayMiddle = lazy(() => import('@/pages/ali-pay-middle'));
const PayResult = lazy(() => import('@/pages/pay-result'));
const HelpPay = lazy(() => import('@/pages/help-pay'));
const PackageCourseDetail = lazy(() => import('@/pages/package-course-detail'));
const BookStudy = lazy(() => import('@/pages/book-study-by-self/BookStudy'));
const BookStudySuccess = lazy(() => import('@/pages/book-study-by-self/BookStudySuccess'));
const RefundRecord = lazy(() => import('@/pages/refund-record'));
const Logistics = lazy(() => import('@/pages/logistics-detail'));
const QuestionnaireResult = lazy(() => import('@/pages/app-page/questionnaire-result/index'));

const TieChuHome = lazy(() => import('@/pages/tiechu/Home'));
const TieChuStudentLogin = lazy(() => import('@/pages/tiechu/StudentLogin'));
const TieChuClassroom = lazy(() => import('@/pages/tiechu/Classroom'));
const TieChuTeacher = lazy(() => import('@/pages/tiechu/TeacherDashboard'));
const SchoolTeacherLogin = lazy(() => import('@/pages/school/login'));
const MathTeacherLayout = lazy(() => import('@/pages/school/math/teacher/MathTeacherLayout'));
const MathWorkbench = lazy(() => import('@/pages/school/math/teacher/workbench'));
const MathLearningTasks = lazy(() => import('@/pages/school/math/teacher/learning-tasks'));
const MathLearningTaskNew = lazy(() => import('@/pages/school/math/teacher/learning-tasks/new'));
const MathLearningTaskDetail = lazy(() => import('@/pages/school/math/teacher/learning-tasks/detail'));
const MathGradingRedirect = lazy(() => import('@/pages/school/math/teacher/grading'));
const MathLearningTaskReview = lazy(() => import('@/pages/school/math/teacher/learning-tasks/review'));
const MathLearningTaskReceipts = lazy(() => import('@/pages/school/math/teacher/learning-tasks/receipts'));
const MathDailyReviewList = lazy(() => import('@/pages/school/math/teacher/learning-tasks/daily-review'));
const MathDailyReviewAnalysis = lazy(() => import('@/pages/school/math/teacher/learning-tasks/daily-review/analysis'));
const MathDailyReviewEvidence = lazy(() => import('@/pages/school/math/teacher/learning-tasks/daily-review/evidence'));
const MathDailyReviewH5 = lazy(() => import('@/pages/school/math/teacher/daily-review-h5'));
const MathQuestionBank = lazy(() => import('@/pages/school/math/teacher/question-bank'));
const MathQuestionBankPapers = lazy(() => import('@/pages/school/math/teacher/question-bank/papers'));
const MathQuestionBankQuestions = lazy(() => import('@/pages/school/math/teacher/question-bank/questions'));
const MathQuestionBankCompose = lazy(() => import('@/pages/school/math/teacher/question-bank/compose'));
const MathQuestionBankComposeNew = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/new'));
const MathQuestionBankComposeResult = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/result'));
const MathQuestionBankComposeEdit = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/edit'));
const MathQuestionBankComposeLayout = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/layout'));
const MathQuestionBankComposeComplete = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/complete'));
const MathQuestionBankComposeRecords = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/records'));
const MathQuestionBankComposeRecordDetail = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/recordDetail'));
const MathQuestionBankComposePackage = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/package'));
const MathQuestionBankComposeGrouping = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/grouping'));
const MathQuestionBankComposePublish = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/publish'));
const MathQuestionBankComposeOverview = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/overview'));
const MathQuestionBankComposeStudent = lazy(() => import('@/pages/school/math/teacher/question-bank/compose/student'));
const MathQuestionBankPaperDetail = lazy(() => import('@/pages/school/math/teacher/question-bank/paperDetail'));
const MathTeachingAssets = lazy(() => import('@/pages/school/math/teacher/teaching-assets'));
const MathResourceCenterLayout = lazy(() => import('@/pages/school/math/teacher/resource-center/ResourceCenterLayout'));
const MathResourceCenter = lazy(() => import('@/pages/school/math/teacher/resource-center'));
const MathResourceCenterUpload = lazy(() => import('@/pages/school/math/teacher/resource-center/upload'));
const MathResourceCenterDetail = lazy(() => import('@/pages/school/math/teacher/resource-center/detail'));
const MathStudentRecords = lazy(() => import('@/pages/school/math/teacher/student-records'));
const MathStudentRecordDetail = lazy(() => import('@/pages/school/math/teacher/student-records/detail'));
const MathStudentEvaluation = lazy(() => import('@/pages/school/math/teacher/student-evaluation'));
const MathGrading = lazy(() => import('@/pages/school/math/teacher/grading'));
const MathMiniTeacher = lazy(() => import('@/pages/school/math/teacher/mini-teacher'));
const MathPeriodReview = lazy(() => import('@/pages/school/math/teacher/period-review'));
const MathRules = lazy(() => import('@/pages/school/math/teacher/rules'));
const PrintDeskPresetAudit = lazy(() => import('@/pages/printdesk/preset-audit'));
const PrintDeskFeedbackList = lazy(() => import('@/pages/printdesk/feedback'));
const PrintDeskFeedbackDetail = lazy(() => import('@/pages/printdesk/feedback/detail'));
const PrintDeskUsage = lazy(() => import('@/pages/printdesk/usage'));
const SchoolEnShell = lazy(() => import('@/pages/school/en/SchoolEnShell'));
const SchoolEnHome = lazy(() => import('@/pages/school/en/SchoolEnHome'));
const SchoolEnAssign = lazy(() => import('@/pages/school/en/SchoolEnAssign'));
const SchoolEnHomeworkReport = lazy(() => import('@/pages/school/en/SchoolEnHomeworkReport'));
const SchoolEnStudentHomeworkReport = lazy(
  () => import('@/pages/school/en/SchoolEnStudentHomeworkReport'),
);
const SchoolEnLearningReport = lazy(() => import('@/pages/school/en/SchoolEnLearningReport'));
const SchoolEnLearningReportView = lazy(() => import('@/pages/school/en/SchoolEnLearningReportView'));
const SchoolEnHomeworkHistory = lazy(() => import('@/pages/school/en/SchoolEnHomeworkHistory'));
const SchoolEnPlaceholder = lazy(() => import('@/pages/school/en/SchoolEnPlaceholder'));

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? (
      <div style={{ padding: 16 }}>
        加载失败，<a onClick={() => location.reload()}>点击重试</a>
      </div>
    ) : (
      this.props.children
    );
  }
}

// 活动页路由布局组件
const ActivePagesLayout = () => {
  return (
    <div className="ignore-rem-max-width-450">
      <PageTransition>
        <Outlet />
      </PageTransition>
    </div>
  );
};

// 包装组件，为页面添加转场动画
const withPageTransition = (Component: React.ComponentType) => (
  <PageTransition>
    <Component />
  </PageTransition>
);

// 包装KeepAlive组件
const withKeepAliveAndTransition = (Component: React.ComponentType, name: string) => (
  <KeepAliveTransition name={name}>
    <Component />
  </KeepAliveTransition>
);

// 包装需要登录的页面
const withAuth = (Component: React.ComponentType) => (
  <RouteGuard requireAuth={true}>
    <PageTransition>
      <Component />
    </PageTransition>
  </RouteGuard>
);

// 包装需要登录且使用 KeepAlive 的页面
const withAuthAndKeepAlive = (Component: React.ComponentType, name: string) => (
  <RouteGuard requireAuth={true}>
    <KeepAliveTransition name={name}>
      <Component />
    </KeepAliveTransition>
  </RouteGuard>
);

// 包装不需要登录的页面
// const withPublic = (Component: React.ComponentType) => (
//   <RouteGuard requireAuth={false}>
//     <PageTransition>
//       <Component />
//     </PageTransition>
//   </RouteGuard>
// );

export default function Router() {
  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate to="/tiechu" replace />,
    },
    {
      path: '/home',
      element: withPageTransition(HomeLayout),
      children: [
        { path: 'study', element: <Study /> },
        { path: 'profile', element: <Profile /> },
        { index: true, element: <Navigate to="/home/profile" replace /> },
      ],
    },
    // 提供给app的页面
    {
      path: '/app-page',
      children: [
        {
          path: 'questionnaire-result',
          element: withPageTransition(QuestionnaireResult),
        },
      ],
    },
    // 活动页路由配置，后面所有的活动页都放到activePages文件里面
    {
      path: '/active-page',
      element: <ActivePagesLayout />,
      children: activePages,
    },
    // 个人信息 - 需要登录
    { path: '/personal-info', element: withAuth(PersonalInfo) },
    // 修改昵称 - 需要登录
    { path: '/change-nickname', element: withAuth(ChangeNickname) },
    // 客服中心 - 需要登录
    { path: '/call-center', element: withAuth(CallCenter) },
    // 意见反馈 - 需要登录
    { path: '/suggestion', element: withAuth(Suggestion) },
    // 地址列表 - 需要登录
    { path: '/address-list', element: withAuth(AddressList) },
    // 地址编辑 - 需要登录
    { path: '/address-edit', element: withAuth(AddressEdit) },
    // 设置 - 需要登录
    { path: '/setting', element: withAuth(Setting) },
    // 服务协议 - 不需要登录
    { path: '/protocol', element: withPageTransition(Protocol) },
    // 注销账号 - 需要登录
    { path: '/cancel-account', element: withAuth(CancelAccount) },
    // 注销账号确认 - 需要登录
    { path: '/cancel-account-confirm', element: withAuth(CancelAccountConfirm) },
    // 当前绑定手机号 - 需要登录
    { path: '/current-phone', element: withAuth(CurrentPhone) },
    // 当前绑定手机号 - 需要登录
    { path: '/study-calendar', element: withAuth(StudyCalendar) },
    { path: '/live-play', element: withAuth(LivePlay) },
    // 验证当前手机号 - 需要登录
    {
      path: '/verify-current-phone',
      element: withAuthAndKeepAlive(VerifyCurrentPhone, 'verify-current-phone'),
    },
    // 验证新手机号 - 需要登录
    {
      path: '/modify-phone',
      element: withAuth(ModifyPhone),
    },
    // 我的订单 - 需要登录
    { path: '/my-order', element: withAuthAndKeepAlive(MyOrder, 'my-order') },
    { path: '/order-detail', element: withAuth(OrderDetail) },
    { path: '/refund-record', element: withPageTransition(RefundRecord) },
    { path: '/logistics-detail', element: withPageTransition(Logistics) },
    // login - 不需要登录
    {
      path: '/login',
      element: withKeepAliveAndTransition(Login, 'login'),
    },
    { path: '/sms-verify', element: withPageTransition(SmsVerify) },
    // 修改密码 - 需要登录
    { path: '/modify-pwd', element: withAuth(ModifyPwd) },
    // 忘记密码 - 不需要登录
    { path: '/forget-pwd', element: withPageTransition(ForgetPwd) },
    // 续费课程优惠券 - 需要登录
    {
      path: '/renew-class-coupon',
      element: withKeepAliveAndTransition(RenewClassCoupon, 'renew-class-coupon'),
    },
    // 体验课领课 - 不需要登录
    { path: '/trial-course', element: withKeepAliveAndTransition(TrialCourse, 'trial-course') },
    // 下载APP - 不需要登录
    { path: '/download-app', element: withPageTransition(DownloadApp) },
    {
      path: '/printdesk/preset-audit',
      element: withPageTransition(PrintDeskPresetAudit),
    },
    {
      path: '/printdesk/feedback',
      element: withPageTransition(PrintDeskFeedbackList),
    },
    {
      path: '/printdesk/feedback/:id',
      element: withPageTransition(PrintDeskFeedbackDetail),
    },
    {
      path: '/printdesk/usage',
      element: withPageTransition(PrintDeskUsage),
    },
    { path: '/course-detail', element: withPageTransition(CourseDetail) },
    { path: '/packageCourseDetail', element: withPageTransition(PackageCourseDetail) },
    { path: '/pay-confirm', element: withAuth(PayConfirm) },
    { path: '/pay', element: withAuth(Pay) },
    { path: '/pay-result', element: withPageTransition(PayResult) },
    // 自主约课相关页面 - 需要登录
    { path: '/book-study-by-self', element: withAuth(BookStudy) },
    { path: '/book-study-by-self/success', element: withAuth(BookStudySuccess) },
    // 铁杵成针 AI课堂（父级 Outlet 必须存在，否则 /tiechu/login、/tiechu/classroom 等子路由无法渲染）
    {
      path: '/tiechu',
      element: (
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      ),
      children: [
        { index: true, element: <Suspense><TieChuHome /></Suspense> },
        { path: 'login', element: <Suspense><TieChuStudentLogin /></Suspense> },
        { path: 'classroom/:classroomId', element: <Suspense><TieChuClassroom /></Suspense> },
        { path: 'classroom', element: <Suspense><TieChuClassroom /></Suspense> },
        { path: 'teacher', element: <Suspense><TieChuTeacher /></Suspense> },
      ],
    },
    {
      path: '/school/login',
      element: (
        <Suspense fallback={null}>
          <SchoolTeacherLogin />
        </Suspense>
      ),
    },
    {
      path: '/school',
      element: <Navigate to="/school/login" replace />,
    },
    {
      path: '/school/math',
      element: (
        <Suspense fallback={null}>
          <MathTeacherLayout />
        </Suspense>
      ),
      children: [
        { index: true, element: <Navigate to="/school/math/workbench" replace /> },
        { path: 'workbench', element: <Suspense><MathWorkbench /></Suspense> },
        { path: 'learning-tasks', element: <Suspense><MathLearningTasks /></Suspense> },
        { path: 'learning-tasks/daily-review', element: <Suspense><MathDailyReviewList /></Suspense> },
        { path: 'learning-tasks/daily-review/:batchId/students/:studentId', element: <Suspense><MathDailyReviewEvidence /></Suspense> },
        { path: 'learning-tasks/daily-review/:batchId', element: <Suspense><MathDailyReviewAnalysis /></Suspense> },
        { path: 'daily-review/h5', element: <Suspense><MathDailyReviewH5 /></Suspense> },
        { path: 'learning-tasks/new', element: <Suspense><MathLearningTaskNew /></Suspense> },
        { path: 'learning-tasks/:taskId/items/:itemId/grading', element: <Suspense><MathGradingRedirect /></Suspense> },
        { path: 'learning-tasks/:taskId/grading', element: <Suspense><MathGradingRedirect /></Suspense> },
        { path: 'learning-tasks/:taskId/items/:itemId/review', element: <Suspense><MathLearningTaskReview /></Suspense> },
        { path: 'learning-tasks/:taskId/review', element: <Suspense><MathLearningTaskReview /></Suspense> },
        { path: 'learning-tasks/:taskId/receipts', element: <Suspense><MathLearningTaskReceipts /></Suspense> },
        { path: 'learning-tasks/:taskId', element: <Suspense><MathLearningTaskDetail /></Suspense> },
        { path: 'student-evaluation', element: <Suspense><MathStudentEvaluation /></Suspense> },
        { path: 'question-bank', element: <Suspense><MathQuestionBank /></Suspense>, children: [
          { index: true, element: <Navigate to="papers" replace /> },
          { path: 'papers', element: <Suspense><MathQuestionBankPapers /></Suspense> },
          { path: 'papers/:paperId', element: <Suspense><MathQuestionBankPaperDetail /></Suspense> },
          { path: 'questions', element: <Suspense><MathQuestionBankQuestions /></Suspense> },
          { path: 'compose', children: [
            { index: true, element: <Suspense><MathQuestionBankCompose /></Suspense> },
            { path: 'new', element: <Suspense><MathQuestionBankComposeNew /></Suspense> },
            { path: 'result', element: <Suspense><MathQuestionBankComposeResult /></Suspense> },
            { path: 'edit', element: <Suspense><MathQuestionBankComposeEdit /></Suspense> },
            { path: 'layout', element: <Suspense><MathQuestionBankComposeLayout /></Suspense> },
            { path: 'complete', element: <Suspense><MathQuestionBankComposeComplete /></Suspense> },
            { path: 'grouping', element: <Suspense><MathQuestionBankComposeGrouping /></Suspense> },
            { path: 'records', element: <Suspense><MathQuestionBankComposeRecords /></Suspense> },
            { path: 'records/:recordId', element: <Suspense><MathQuestionBankComposeRecordDetail /></Suspense> },
            { path: 'records/:recordId/package', element: <Suspense><MathQuestionBankComposePackage /></Suspense> },
            { path: 'publish', element: <Suspense><MathQuestionBankComposePublish /></Suspense> },
            { path: 'overview', element: <Suspense><MathQuestionBankComposeOverview /></Suspense> },
            { path: 'student/:studentId', element: <Suspense><MathQuestionBankComposeStudent /></Suspense> },
          ] },
        ] },
        { path: 'teaching-assets', element: <Suspense><MathTeachingAssets /></Suspense> },
        { path: 'teaching-assets/unit/:unitId', element: <Suspense><MathTeachingAssets /></Suspense> },
        { path: 'teaching-assets/:knowledgePointId', element: <Suspense><MathTeachingAssets /></Suspense> },
        { path: 'resource-center', element: <Suspense><MathResourceCenterLayout /></Suspense>, children: [
          { index: true, element: <Suspense><MathResourceCenter /></Suspense> },
          { path: 'upload', element: <Suspense><MathResourceCenterUpload /></Suspense> },
        ] },
        { path: 'resource-center/:materialId', element: <Suspense><MathResourceCenterDetail /></Suspense> },
        { path: 'student-records', element: <Suspense><MathStudentRecords /></Suspense> },
        { path: 'student-records/:studentId', element: <Suspense><MathStudentRecordDetail /></Suspense> },
        { path: 'grading', element: <Suspense><MathGrading /></Suspense> },
        { path: 'mini-teacher', element: <Suspense><MathMiniTeacher /></Suspense> },
        { path: 'period-review', element: <Suspense><MathPeriodReview /></Suspense> },
        { path: 'rules', element: <Suspense><MathRules /></Suspense> },
      ],
    },
    {
      path: '/school/en',
      element: (
        <Suspense fallback={null}>
          <SchoolEnShell />
        </Suspense>
      ),
      children: [
        { index: true, element: <Suspense><SchoolEnHome /></Suspense> },
        { path: 'assign', element: <Suspense><SchoolEnAssign /></Suspense> },
        { path: 'learning-report', element: <Suspense><SchoolEnLearningReport /></Suspense> },
        { path: 'learning-report/view', element: <Suspense><SchoolEnLearningReportView /></Suspense> },
        { path: 'history', element: <Suspense><SchoolEnHomeworkHistory /></Suspense> },
        { path: 'homework/:assignmentId', element: <Suspense><SchoolEnHomeworkReport /></Suspense> },
        {
          path: 'homework/:assignmentId/students/:studentId',
          element: <Suspense><SchoolEnStudentHomeworkReport /></Suspense>,
        },
        { path: 'agent', element: <Suspense><SchoolEnPlaceholder /></Suspense> },
        { path: 'exam', element: <Suspense><SchoolEnPlaceholder /></Suspense> },
        { path: 'settings', element: <Suspense><SchoolEnPlaceholder /></Suspense> },
        { path: 'feedback', element: <Suspense><SchoolEnPlaceholder /></Suspense> },
        { path: 'messages/:threadId', element: <Suspense><SchoolEnPlaceholder /></Suspense> },
      ],
    },
    { path: '/404', element: withPageTransition(NotFound) },
    { path: '/ali-pay-middle', element: withPageTransition(AliPayMiddle) },
    { path: '/helpPay', element: withPageTransition(HelpPay) },
    // 唤醒app页面 - 不需要登录
    { path: '/awaken-app', element: withPageTransition(AwakenApp) },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
  return (
    <RouteErrorBoundary>
      <Suspense>{routes}</Suspense>
    </RouteErrorBoundary>
  );
}
